/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Icon,
  Modal,
  Popup,
  Segment,
  Form,
  Radio,
  Message,
} from 'semantic-ui-react';

import { Formik } from 'formik';
import { isEmpty, get, compact } from 'lodash';

import {
  CustomInput,
  CustomTextArea,
  CustomMultiSelectDropdown,
} from '../../../../components/form';

import { CustomTable } from '../../../../components';

import {
  getOrganizationGoals,
  getDepartmentHierarchy,
  getDepartmentLabels,
  postOrganisationDepartment,
  deleteOrganisationDepartment,
  updateOrganizationDepartment,
  updateDepartmentLabels,
  addDepartmentAdmin,
  deleteDepartmentAdmin,
} from '../../../../store/actions';

import { UserSearchDropdown } from '../../../../components/users';

import {
  deptValidationSchema,
  deptInitialValues,
} from '../../../../validations/orgSettingsValidation';

import { ViewListModal } from '../../../../components';

import { stopPropagation } from '../../../../utils';
import { MAX_DEPT_LEVELS } from '../../../../utils/constant';
import { DeptService } from '../../../../services';

import '../organization.scss';
import './Departments.scss';

const AddDeptModal = (props) => {
  const { t } = useTranslation();
  const { open, onClose, parentDept, deptLabels, orgGoals } = props;
  const dispatch = useDispatch();

  const [selectedGoals, setSelectedGoals] = useState([]);
  const [specificGoalSelected, setSpecificGoalSelected] = useState(false);

  const parentDerivedGoals = parentDept?.derivedGoals ?? orgGoals;

  useEffect(() => {
    setSpecificGoalSelected(false);
  }, []);

  const renderGoalDropdownValues = () => {
    return parentDerivedGoals.map((goal, key) => {
      return {
        key,
        value: goal.id,
        text: goal.goalTitle,
      };
    });
  };

  const handleSubmit = async (values, setSubmitting) => {
    try {
      delete values.goalTitle;

      if (!specificGoalSelected) {
        delete values.goals;
      }
      values.parentDepartmentId = parentDept?.id || null;
      await dispatch(postOrganisationDepartment(values));

      onClose();
    } catch (e) {
      setSubmitting(false);
    }
  };

  const getGoalDropdownData = (values) => {
    setSelectedGoals(values);
  };

  const onSelectedSpecificGoal = () => {
    setSpecificGoalSelected(!specificGoalSelected);
    if (!isEmpty(selectedGoals)) {
      setSelectedGoals([]);
    }
  };

  const deptLabel = DeptService.getDeptLabelForLevel(
    deptLabels,
    (parentDept?.level || 0) + 1
  );

  return (
    <Modal
      open={open}
      size="small"
      onClick={(e) => stopPropagation(e)}
      onFocus={(e) => stopPropagation(e)}
      closeIcon={<Icon onClick={onClose} name="close" className="closeicon" />}
      className="add-ref-model"
    >
      <Modal.Header>
        {parentDept
          ? `${t('actions.add')} ${deptLabel} {t('actions.to')} "${parentDept.name}"`
          : `${t('actions.add')} ${deptLabel}`}
      </Modal.Header>
      <Modal.Content>
        <Segment>
          <Formik
            initialValues={deptInitialValues()}
            validationSchema={deptValidationSchema(
              t,
              deptLabel,
              specificGoalSelected
            )}
            onSubmit={(values, { setSubmitting }) => {
              handleSubmit(values, setSubmitting);
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              setFieldValue,
              values,
              errors,
            }) => (
              <Form onSubmit={handleSubmit}>
                <CustomInput
                  name="name"
                  id="name"
                  labelText={`${deptLabel} ${t('formulary.name')}`}
                  placeholder={t('formulary.placeholder.enter_name')}
                />

                <CustomTextArea
                  name="description"
                  id="description"
                  labelText={`${deptLabel} ${t('formulary.description')}`}
                  placeholder={t('formulary.placeholder.enter_description')}
                />

                <div className="org three-step-radio-container">
                  <label>
                    {t('organisation.department_and_units.specific_goals')}
                  </label>
                  <Radio
                    toggle
                    checked={specificGoalSelected}
                    onChange={onSelectedSpecificGoal}
                    disabled={isEmpty(parentDerivedGoals)}
                  />
                </div>

                {specificGoalSelected && (
                  <CustomMultiSelectDropdown
                    pointing="top"
                    id="dept-goals-dropdown"
                    name="goals"
                    labelText={t('organisation.department_and_units.goals')}
                    placeholder={t(
                      'organisation.department_and_units.select_goals'
                    )}
                    options={renderGoalDropdownValues()}
                    getData={getGoalDropdownData}
                    values={values}
                    defaultValues={selectedGoals}
                    setFieldValue={setFieldValue}
                    isView={false}
                  />
                )}

                {!specificGoalSelected && (
                  <div className="msg-report">
                    <Message
                      color="blue"
                      icon="exclamation circle"
                      content={
                        parentDerivedGoals.length === orgGoals.length
                          ? t(
                              'organisation.department_and_units.all_org_goals_are_available_for_selection'
                            )
                          : t(
                              'organisation.department_and_units.some_org_goals_are_available_for_selection'
                            )
                      }
                    />
                  </div>
                )}

                <div className="model-buttons">
                  <Button
                    className="Secondary-Button"
                    type="button"
                    onClick={onClose}
                  >
                    {t('actions.cancel')}
                  </Button>

                  <Button
                    content={t('actions.add')}
                    type="submit"
                    className="Primary-Button"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Segment>
      </Modal.Content>
    </Modal>
  );
};

const AddDeptViaMenuItem = ({ parentDept, deptLabels, ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (DeptService.isYoungestDeptLevel(deptLabels, parentDept.level)) {
    return null;
  }

  return (
    <>
      <span className="add-dept-label" onClick={() => setOpen(true)}>
        {t('actions.add')}{' '}
        {DeptService.getDeptLabelForLevel(
          deptLabels,
          (parentDept?.level || 0) + 1
        )}
      </span>
      <AddDeptModal
        open={open}
        onClose={() => setOpen(false)}
        parentDept={parentDept}
        deptLabels={deptLabels}
        {...props}
      />
    </>
  );
};

const EditDepartmentModal = (props) => {
  const { t } = useTranslation();
  const { deptToEdit, deptHierarchy, deptLabels, orgGoals, open, onClose } =
    props;

  const dispatch = useDispatch();

  const [editSpecificGoal, setEditSpecificGoal] = useState(
    !deptToEdit.isOrganisationGoal
  );

  const deptLevel = deptToEdit.level;
  const parentDeptId = deptToEdit.parentDepartmentId;
  const parentDept = DeptService.getDeptById(parentDeptId, deptHierarchy);
  const parentDerivedGoals = parentDept?.derivedGoals ?? orgGoals;

  const renderGoalDropdownValues = () =>
    !isEmpty(parentDerivedGoals)
      ? parentDerivedGoals.map((data, key) => ({
          key,
          value: data.id,
          rest: { key: data.id },
          text: !isEmpty(data.goalTitle)
            ? data.goalTitle
            : get(data, 'goals.goalTitle', ''),
        }))
      : [];

  const goalDefaultValues = (values) =>
    !isEmpty(parentDerivedGoals)
      ? compact(
          parentDerivedGoals.map((data) => {
            if (!values['goals'] || !values['goals'].includes(data.id)) {
              return null;
            }
            return data.goalTitle;
          })
        )
      : [];

  const deptLabel = DeptService.getDeptLabelForLevel(
    deptLabels,
    deptToEdit.level
  );

  const handleSubmit = async (values) => {
    const deptId = deptToEdit.id;

    delete values.goalTitle;
    if (!editSpecificGoal) {
      delete values.goals;
    }
    await dispatch(updateOrganizationDepartment(values, deptId));
    onClose();
  };

  return (
    <Modal
      open={open}
      size="small"
      onClick={(e) => stopPropagation(e)}
      onFocus={(e) => stopPropagation(e)}
      closeIcon={<Icon onClick={onClose} name="close" className="closeicon" />}
      className="add-ref-model"
    >
      <Modal.Header>
        {`${t('actions.edit')} ${deptLabel}: "${deptToEdit.name}"`}
      </Modal.Header>
      <Modal.Content>
        <Segment>
          <Formik
            initialValues={deptInitialValues(deptToEdit)}
            validationSchema={deptValidationSchema(
              t,
              DeptService.getDeptLabelForLevel(deptLabels, deptLevel),
              true,
              true
            )}
            onSubmit={(values) => handleSubmit(values)}
          >
            {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
              <Form onSubmit={handleSubmit}>
                <CustomInput
                  name="name"
                  id="name"
                  labelText={`${deptLabel} ${t('formulary.name')}`}
                  placeholder={t('formulary.placeholder.enter_name')}
                />

                <CustomTextArea
                  name="description"
                  id="description"
                  labelText={`${deptLabel} ${t('formulary.description')}`}
                  placeholder={t('formulary.placeholder.enter_description')}
                  rows="4"
                />

                <div className="org three-step-radio-container">
                  <label>
                    {t('organisation.department_and_units.specific_goals')}
                  </label>
                  <Radio
                    toggle
                    checked={editSpecificGoal}
                    onChange={() => setEditSpecificGoal(!editSpecificGoal)}
                  />
                </div>

                {editSpecificGoal && (
                  <CustomMultiSelectDropdown
                    pointing="top"
                    id="dept-goals-dropdown"
                    name="goals"
                    labelText={t('common.goals')}
                    placeholder={t(
                      'organisation.department_and_units.select_goals'
                    )}
                    options={renderGoalDropdownValues()}
                    values={values}
                    defaultValuesTexts={goalDefaultValues(values)}
                    setFieldValue={setFieldValue}
                    isView={false}
                  />
                )}

                {!editSpecificGoal && (
                  <div className="msg-report">
                    <Message
                      color="blue"
                      icon="exclamation circle"
                      content={
                        parentDerivedGoals.length === orgGoals.length
                          ? t(
                              'organisation.department_and_units.all_org_goals_are_available_for_selection'
                            )
                          : t(
                              'organisation.department_and_units.some_org_goals_are_available_for_selection'
                            )
                      }
                    />
                  </div>
                )}

                <div className="model-buttons">
                  <Button
                    className="Secondary-Button"
                    type="button"
                    onClick={onClose}
                  >
                    {t('actions.cancel')}
                  </Button>

                  <Button
                    content={t('actions.save')}
                    type="submit"
                    className="Primary-Button"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Segment>
      </Modal.Content>
    </Modal>
  );
};

const EditDepartmentMenuItem = (props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <label
        onClick={() => setOpen(true)}
        className="org-users-actions-warn default-color"
      >
        {t('actions.edit')}
      </label>
      <EditDepartmentModal
        {...props}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const DeleteDepartmentModal = ({ deptToDelete, open, onClose, ...props }) => {
  const { t } = useTranslation();
  const { deptLabels } = props;
  const label = DeptService.getDeptLabelForLevel(
    deptLabels,
    deptToDelete.level
  );

  const dispatch = useDispatch();

  const loading = useSelector(
    (state) => state.organization.deleteDepartmentLoading
  );

  const handleClick = async () => {
    await dispatch(deleteOrganisationDepartment(deptToDelete.id));
    onClose();
  };

  return (
    <Modal
      open={open}
      closeIcon={<Icon onClick={onClose} name="close" className="closeicon" />}
      size="mini"
    >
      <Modal.Header>{`${t('actions.remove')} ${label}: "${deptToDelete.name}"`}</Modal.Header>
      <Modal.Content>
        <p>
          {t('actions.are_you_sure_you_want_to_remove')} {label.toLowerCase()} "
          {deptToDelete.name}"?
        </p>
        <p>{t('actions.this_action_cannot_be_undone')}</p>
      </Modal.Content>
      <Modal.Actions>
        <Button disabled={loading} onClick={onClose} className="modal-cancel">
          {t('actions.no')}
        </Button>
        <Button
          onClick={handleClick}
          loading={loading}
          disabled={loading}
          className="modal-proceed"
          content={t('actions.delete')}
        />
      </Modal.Actions>
    </Modal>
  );
};

const DeleteDepartmentMenuItem = ({ deptToDelete, ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div onClick={() => setOpen(true)} className="remove-department">
        {t('actions.remove')}
      </div>
      <DeleteDepartmentModal
        open={open}
        onClose={() => setOpen(false)}
        deptToDelete={deptToDelete}
        {...props}
      />
    </>
  );
};

const DeptAdminsModal = ({
  open,
  onClose,
  dept,
  addDepartmentAdmin,
  deleteDepartmentAdmin,
}) => {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [adding, setAdding] = useState(false);

  const admins = dept?.admins ?? [];

  const handleAdd = async () => {
    if (!selectedUser) {
      return;
    }
    setAdding(true);
    await addDepartmentAdmin({ deptId: dept.id, userId: selectedUser.id });
    setSelectedUser(null);
    setAdding(false);
  };

  const handleRemove = async (userId) => {
    await deleteDepartmentAdmin({ deptId: dept.id, userId });
  };

  return (
    <Modal
      open={open}
      size="small"
      closeIcon={<Icon onClick={onClose} name="close" className="closeicon" />}
    >
      <Modal.Header>
        {t('common.admins')} — {dept?.name || '?'}
      </Modal.Header>
      <Modal.Content>
        <div className="dept-admins-search-row">
          <UserSearchDropdown
            value={selectedUser}
            onChange={setSelectedUser}
            placeholder={`${t('search.search_by_name_or_email')}…`}
          />
          <Button
            className="Primary-Button"
            onClick={handleAdd}
            disabled={!selectedUser || adding}
            loading={adding}
          >
            {t('actions.add')}
          </Button>
        </div>
        {admins.length === 0 ? (
          <p className="dept-admins-empty">
            {t('organisation.department_and_units.no_admins_assigned')}
          </p>
        ) : (
          <ul className="dept-admins-modal-list">
            {admins.map((admin) => (
              <li key={admin.id} className="dept-admins-modal-item">
                <div className="dept-admins-modal-info">
                  <span className="dept-admins-modal-name">
                    {admin.firstName} {admin.surname}
                  </span>
                  <span className="dept-admins-modal-email">{admin.email}</span>
                </div>
                <Icon
                  name="trash alternate outline"
                  className="dept-admins-modal-remove"
                  onClick={() => handleRemove(admin.id)}
                  title={t('organisation.department_and_units.remove_admin')}
                />
              </li>
            ))}
          </ul>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button className="Secondary-Button" onClick={onClose}>
          {t('actions.close')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const DeptAdminsMenuItem = ({
  dept,
  addDepartmentAdmin,
  deleteDepartmentAdmin,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <span className="dept-admins-edit-link" onClick={() => setOpen(true)}>
        {t('actions.edit')}
      </span>
      <DeptAdminsModal
        open={open}
        onClose={() => setOpen(false)}
        dept={dept}
        addDepartmentAdmin={addDepartmentAdmin}
        deleteDepartmentAdmin={deleteDepartmentAdmin}
      />
    </>
  );
};

const EditLabelsModal = ({
  open,
  onClose,
  deptLabels,
  updateDepartmentLabels,
}) => {
  const { t } = useTranslation();

  const buildRows = () =>
    Array.from({ length: MAX_DEPT_LEVELS }, (_, i) => {
      const existing = deptLabels?.find((l) => l.level === i + 1);
      return existing
        ? { label: existing.label, plural: existing.plural }
        : { label: '', plural: '' };
    });

  const [rows, setRows] = useState(buildRows);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(buildRows());
    }
  }, [open, deptLabels]);

  const handleChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    const labels = rows
      .map((row, i) => ({
        level: i + 1,
        label: row.label.trim(),
        plural: row.plural.trim(),
      }))
      .filter((l) => l.label !== '' || l.plural !== '');
    await updateDepartmentLabels(labels);
    setSaving(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      size="small"
      closeIcon={<Icon onClick={onClose} name="close" className="closeicon" />}
    >
      <Modal.Header>
        {t('organisation.department_and_units.edit_dept_labels')}
      </Modal.Header>
      <Modal.Content>
        <div className="edit-labels-grid">
          <div className="edit-labels-header">
            <span>{t('organisation.department_and_units.level')}</span>
            <span>{t('organisation.department_and_units.label_singular')}</span>
            <span>{t('organisation.department_and_units.label_plural')}</span>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="edit-labels-row">
              <span className="edit-labels-level">{i + 1}</span>
              <input
                className="edit-labels-input"
                value={row.label}
                placeholder={`${t('common.for_example_abbrev')} ${t('common.department')}`}
                onChange={(e) => handleChange(i, 'label', e.target.value)}
              />
              <input
                className="edit-labels-input"
                value={row.plural}
                placeholder={`${t('common.for_example_abbrev')} ${t('common.departments')}`}
                onChange={(e) => handleChange(i, 'plural', e.target.value)}
              />
            </div>
          ))}
        </div>
        <p className="edit-labels-hint">
          {t(
            'organisation.department_and_units.leave_row_blank_to_remove_label'
          )}
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button
          className="Secondary-Button"
          onClick={onClose}
          disabled={saving}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          className="Primary-Button"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        >
          {t('actions.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const EditLabelsMenuItem = ({ deptLabels, updateDepartmentLabels }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        className="Secondary-Button edit-labels-btn"
        onClick={() => setOpen(true)}
      >
        <Icon name="edit" />
        {t('organisation.department_and_units.edit_labels')}
      </Button>
      <EditLabelsModal
        open={open}
        onClose={() => setOpen(false)}
        deptLabels={deptLabels}
        updateDepartmentLabels={updateDepartmentLabels}
      />
    </>
  );
};

let Departments = (props) => {
  const { t } = useTranslation();
  const { isSuperAdmin, deptHierarchy, deptLabels } = props;
  const [addDeptOpen, setAddDeptOpen] = useState(false);

  const columns = [
    {
      // title: `${DeptService.getDeptLabelForTopLevel(deptLabels)} Name`,
      title: t('common.name'),
      width: 3,
      render: (data, level) => {
        return (
          <div className="dept-name">
            <label>
              {data.name}
              {isSuperAdmin && <span className="id">#{data.id}</span>}
            </label>
          </div>
        );
      },
    },
    {
      title: t('common.goals'),
      width: 3,
      render: (data, level) => {
        return (
          <div className="goal-info">
            <label>
              {data.departmentGoals.length > 0 ? (
                <span>
                  {t('common.goal_w_num', {
                    count: data.departmentGoals.length,
                  }).toLowerCase()}
                </span>
              ) : data.derivedGoalsIsOrg ? (
                <span className="all-org">
                  {t('organisation.department_and_units.all_org_goals')}
                </span>
              ) : (
                <span className="inherited">
                  {data.derivedGoals.length}{' '}
                  {t('organisation.department_and_units.inherited_goals')}
                </span>
              )}
            </label>
            {data.departmentGoals.length > 0 && (
              <ViewListModal
                title={`${DeptService.getDeptLabelForLevel(
                  deptLabels,
                  data.level
                )} ${t('common.goals')}`}
                data={data.departmentGoals}
                extractor={(elem) => elem.goalTitle || '-'}
              />
            )}
          </div>
        );
      },
    },
    {
      title: t('common.users'),
      width: 3,
      render: (data, level) => {
        return (
          <div className="users">
            <label>
              {data.totalUsersAssociated === 0 ? (
                <span className="none">-</span>
              ) : data.totalUsersAssociated > data.departmentUsers.length ? (
                <span className="dift">
                  {data.departmentUsers.length}{' '}
                  <span className="lbl">
                    {t('organisation.department_and_units.direct')}
                  </span>
                  {} / {}
                  {data.totalUsersAssociated}{' '}
                  <span className="lbl">{t('common.total')}</span>
                </span>
              ) : (
                <span className="same">
                  {data.departmentUsers.length}{' '}
                  <span className="lbl">
                    {t('organisation.department_and_units.direct')}
                  </span>
                </span>
              )}
            </label>
          </div>
        );
      },
    },
    {
      title: t('common.admins'),
      width: 3,
      render: (data, level) => {
        const admins = data.admins ?? [];
        return (
          <div className="dept-admins">
            {admins.length > 0 && (
              <ul className="dept-admins-list">
                {admins.map((admin) => (
                  <li key={admin.id} className="dept-admins-item">
                    <Popup
                      content={admin.email}
                      trigger={
                        <span className="dept-admins-name">
                          {admin.firstName} {admin.surname}
                        </span>
                      }
                      position="top center"
                      size="small"
                    />
                  </li>
                ))}
              </ul>
            )}
            <DeptAdminsMenuItem
              dept={data}
              addDepartmentAdmin={props.addDepartmentAdmin}
              deleteDepartmentAdmin={props.deleteDepartmentAdmin}
            />
          </div>
        );
      },
    },
    {
      title: '',
      width: 2,
      render: (data, level) => {
        return (
          <div className="action-container">
            <AddDeptViaMenuItem parentDept={data} {...props} />
          </div>
        );
      },
    },
    {
      title: '',
      width: 2,
      render: (data, level) => {
        return (
          <div className="action-container">
            <EditDepartmentMenuItem deptToEdit={data} {...props} />
            <DeleteDepartmentMenuItem deptToDelete={data} {...props} />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    props.getOrganizationGoals();
    props.getDepartmentHierarchy();
    props.getDepartmentLabels();
  }, []);

  return (
    <div>
      <Segment className="org-dept-segment">
        <div>
          <div className="dept-actions-ctr">
            <div className="dept-labels">
              <span className="leadin">
                {t('organisation.department_and_units.categories')}:
              </span>{' '}
              {DeptService.deptLabelsAsConcatenatedLabel(deptLabels) ||
                `[${t('common.none').toLowerCase()}]`}
              <EditLabelsMenuItem
                deptLabels={deptLabels}
                updateDepartmentLabels={props.updateDepartmentLabels}
              />
            </div>
            <div className="add-btn-ctr">
              {deptLabels?.length && (
                <Button
                  onClick={() => setAddDeptOpen(true)}
                  className="Primary-Button add-dept-btn"
                >
                  <Icon name="plus" />
                  {t('actions.add')}{' '}
                  {DeptService.getDeptLabelForTopLevel(deptLabels)}
                </Button>
              )}
            </div>
          </div>
          <AddDeptModal
            open={addDeptOpen}
            onClose={() => setAddDeptOpen(false)}
            parentDept={null}
            {...props}
          />
        </div>

        <CustomTable
          header
          columns={columns}
          data={deptHierarchy}
          customClass="org-depts-table"
          height={true}
          emptyMessage={t(
            'activity.departments.no_departments_have_been_added'
          )}
          keyExtractor={(item, index) => item.id}
        />
      </Segment>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.authentication.user,
    isSuperAdmin: state.authentication.isSuperAdmin,
    deptHierarchy: state.organization.deptHierarchy,
    deptHierarchyFlattened: state.organization.deptHierarchyFlattened,
    deptLabels: state.organization.deptLabels,
    orgGoals: state.organization.organisationGoals,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getOrganizationGoals,
      getDepartmentHierarchy,
      getDepartmentLabels,
      postOrganisationDepartment,
      updateOrganizationDepartment,
      deleteOrganisationDepartment,
      updateDepartmentLabels,
      addDepartmentAdmin,
      deleteDepartmentAdmin,
    },
    dispatch
  );
};

Departments = connect(mapStateToProps, mapDispatchToProps)(Departments);

export { Departments };
